import React, { useState, useRef, useEffect, useCallback } from "react";
import { useWorkplace } from "../context/WorkplaceContext";
import IonIcon from "../components/IonIcon";
import cctvSample from "../images/cctv_sample01.jpg";
import logo from "../images/logo.png";
import weatherIcon from "../images/weather_icon.png";
import "../styles/dashboard_container.css";
import Ruler from "../components/Ruler";
import Modal from "react-modal";
import { useNavigate } from "react-router-dom";
import GasSensor from "../components/GasSensorPanel";
import apiClient from "../apiClient"; // ★ 내 API 호출용

const OWM_KEY = import.meta?.env?.VITE_OWM_KEY || process.env.REACT_APP_OWM_KEY;

const CHUNK_SIZE = 3; // 한 column에 3줄

function chunkArray(arr, size) {
  const res = [];
  for (let i = 0; i < arr.length; i += size) res.push(arr.slice(i, i + size));
  return res;
}

const DashBoardForLocation = () => {
  const selectedWorkplaceId = localStorage.getItem("selectedWorkplaceId"); //useWorkplace(); // Context 사용

  const [now, setNow] = useState(new Date());
  const [groups, setGroups] = useState([]);
  const [stayCnt, setStayCnt] = useState([]);
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [isListPopupOpen, setIsListPopupOpen] = useState(false);
  const [isAboveListPopupOpen, setIsAboveListPopupOpen] = useState(false);
  const [workplace, setWorkplace] = useState(null);
  const [cameraList, setCameraLists] = useState([]);
  const [weather, setWeather] = useState(null);
  const [weatherErr, setWeatherErr] = useState("");
  const [expandedIdx, setExpandedIdx] = useState(null);
  const [groundCount, setGroundCount] = useState(null);
  const [leftIntervals, setLeftIntervals] = useState([]); // direction="rtl"
  const [rightIntervals, setRightIntervals] = useState([]); // direction="ltr"

  const [aboveList, setAboveList] = useState([]);
  const aboveHoverTimerRef = useRef(null);

  const cctvRef = useRef(null);
  const navigate = useNavigate();
  const lastClickRef = useRef({ t: 0, idx: null });
  const DBLCLICK_GAP = 280;

  const cctvIsDragRef = useRef(false);
  const cctvStartedDraggingRef = useRef(false);
  const cctvStartXRef = useRef(0);
  const cctvStartScrollRef = useRef(0);
  const cctvDragThreshold = 18;
  const openAboveListInstant = useCallback(() => {
    if (aboveHoverTimerRef.current) {
      clearTimeout(aboveHoverTimerRef.current);
      aboveHoverTimerRef.current = null;
    }
    setIsAboveListPopupOpen(true);
  }, []);

  const scheduleCloseAboveList = useCallback(() => {
    if (aboveHoverTimerRef.current) clearTimeout(aboveHoverTimerRef.current);
    // 살짝 딜레이 후 닫기(모달로 포인터 이동 시 끊기지 않게)
    aboveHoverTimerRef.current = setTimeout(() => {
      setIsAboveListPopupOpen(false);
      aboveHoverTimerRef.current = null;
    }, 220);
  }, []);

  useEffect(() => {
    return () => {
      if (aboveHoverTimerRef.current) clearTimeout(aboveHoverTimerRef.current);
    };
  }, []);

  const openExpanded = useCallback((idx) => {
    setExpandedIdx(idx);
  }, []);

  const closeExpanded = useCallback(() => {
    setExpandedIdx(null);
  }, []);
  const onTileDoubleClick = useCallback((idx) => {
    // 드래그로 판단된 프레임이면 무시
    if (cctvStartedDraggingRef.current) return;
    console.log("[dblclick]", idx);
    setExpandedIdx(idx);
  }, []);

  const onTileClick = useCallback((idx) => {
    // 드래그로 판단되면 무시
    if (cctvStartedDraggingRef.current) return;

    const now = performance.now();
    const { t: prev, idx: prevIdx } = lastClickRef.current;

    if (prevIdx === idx && now - prev < DBLCLICK_GAP) {
      // '더블클릭'으로 인식
      setExpandedIdx(idx);
      lastClickRef.current = { t: 0, idx: null }; // 초기화
    } else {
      // 첫 클릭 기록
      lastClickRef.current = { t: now, idx };
    }
  }, []);

  useEffect(() => {
    if (expandedIdx !== null) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => {
        document.body.style.overflow = prev;
      };
    }
  }, [expandedIdx]);

  useEffect(() => {
    if (expandedIdx === null) return;
    const onKey = (e) => {
      if (e.key === "Escape") closeExpanded();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [expandedIdx, closeExpanded]);
  const onCctvPointerDown = (e) => {
    const el = cctvRef.current;
    if (!el) return;
    cctvIsDragRef.current = true;
    cctvStartedDraggingRef.current = false;
    cctvStartXRef.current = e.clientX;
    cctvStartScrollRef.current = el.scrollLeft;
    el.classList.add("dragging");
  };

  const onCctvPointerMove = (e) => {
    if (e.buttons === 0) return;
    if (!cctvIsDragRef.current) return;
    const el = cctvRef.current;
    if (!el) return;
    const dx = e.clientX - cctvStartXRef.current;

    if (Math.abs(dx) >= cctvDragThreshold) {
      cctvStartedDraggingRef.current = true;
      el.scrollLeft = cctvStartScrollRef.current - dx; // 드래그한 만큼 가로 스크롤
      e.preventDefault(); // 텍스트 선택 방지
    }
  };

  const onCctvPointerUpOrLeave = () => {
    cctvIsDragRef.current = false;
    cctvStartedDraggingRef.current = false;
    cctvRef.current?.classList.remove("dragging");
  };

  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, []);

  const pollRef = useRef(null);

  useEffect(() => {
    if (!selectedWorkplaceId) return;

    fetchCameras();
    const camTimer = setInterval(fetchCameras, 10000);
    return () => clearInterval(camTimer);
  }, [selectedWorkplaceId]);

  useEffect(() => {
    let abort = false;
    async function loadWorkplaceMeta(id) {
      if (!id) {
        setWorkplace(null);
        console.debug("[workplace] no id");
        return;
      }
      try {
        const { data } = await apiClient.post("/api/WorkPlace/workplace-info", {
          WorkplaceId: id, // ← selectedWorkplaceId 말고 인자 사용
        });
        const res = typeof data === "string" ? JSON.parse(data) : data;

        if (res?.data?.length > 0) {
          const item = res.data[0];
          console.log(item);
          const lat = Number(item.latitude);
          const lon = Number(item.longitude);
          const address = item.addr;
          const drawimg = item.workplaceDraw;
          const left_length = Number(item.leftLength);
          const left_meter_interval = Number(item.leftMeterInterval);
          const left_percent = Number(item.leftPercent);
          const right_length = Number(item.rightLength);
          const right_meter_interval = Number(item.rightMeterInterval);
          const right_percent = Number(item.rightPercent);

          if (!abort) {
            setWorkplace({
              lat,
              lon,
              address,
              drawimg,
              left_length,
              left_meter_interval,
              left_percent,
              right_length,
              right_meter_interval,
              right_percent,
              raw: item,
            });
            console.debug("[workplace] set", {
              lat,
              lon,
              address,
              drawimg,
              left_length,
              left_meter_interval,
              left_percent,
              right_length,
              right_meter_interval,
              right_percent,
            });
          }
        } else {
          if (!abort) setWorkplace(null);
          console.debug("[workplace] empty");
        }
      } catch (e) {
        if (!abort) setWorkplace(null);
        console.error("workplace meta load failed:", e);
      }
    }

    loadWorkplaceMeta(selectedWorkplaceId);
    return () => {
      abort = true;
    };
  }, [selectedWorkplaceId]);

  const fetchCameras = async () => {
    // 실시간 카메라리스트 뿌리기
    try {
      const response = await apiClient.post(
        "/api/RealTimeVideo/all-camera-list",
        {
          Page: 1,
          PageSize: 100,
          OrderType: 1,
          WorkplaceId: selectedWorkplaceId,
          LoginUserId: localStorage.getItem("userid"),
        }
      );

      const responseData =
        typeof response.data === "string"
          ? JSON.parse(response.data)
          : response.data;

      if (!responseData || !responseData.data) {
        console.warn("❌ No camera data found.");
        setCameraLists([]);
        return;
      }

      console.log("📷 카메라 리스트 업데이트", response.data);

      const categories = responseData.data.map((item) => ({
        cameraID: item.camId,
        cameraName: item.camPosition,
        camUrl: item.camURL,
        serverIP: item.serverIP,
        camUUID: item.camUUID,
      }));

      setCameraLists(categories);

      setTimeout(() => {
        categories.forEach((camera, index) => {
          console.log(`📡 WebSocket 재연결 시도: index ${index}`);
          startPlay(index, camera.serverIP, camera.camUUID);
        });
      }, 500); // 초기화 지연 방지
    } catch (error) {
      console.error("🚨 Failed to fetch camera lists:", error);
    }
  };

  const RECONNECT_INTERVAL = 300000; // 5분마다 자동 재연결
  const MAX_SESSION_DURATION = 600000; // 10분마다 강제 재연결
  const MAX_CONNECTIONS = 20; // 최대 WebSocket 개수 제한

  let reconnectTimers = {};
  let pendingBuffers = {};
  let isBufferProcessing = {};

  const startPlay = (index, serverIP, camUUID) => {
    const videoEl = document.getElementById(`mse-video-${index}`);
    if (!videoEl) {
      console.warn(`🚨 Video element not found for index ${index}`);
      return;
    }

    if (
      window[`ws_${index}`] &&
      window[`ws_${index}`].readyState === WebSocket.OPEN
    ) {
      console.warn(`⚠️ WebSocket already open for index ${index}`);
      return;
    }

    const mse = new MediaSource();
    videoEl.src = URL.createObjectURL(mse);

    const useWS = serverIP.includes("192.168.0.2");
    const protocol = useWS ? "ws" : "wss";

    const wsUrl = `${protocol}://${serverIP}/stream/${camUUID}/channel/0/mse?uuid=${camUUID}&channel=0`;

    if (window[`ws_${index}`]) {
      console.warn(`❌ Closing existing WebSocket for index ${index}`);
      window[`ws_${index}`].close();
      delete window[`ws_${index}`];
    }

    let ws = new WebSocket(wsUrl);
    window[`ws_${index}`] = ws;
    ws.binaryType = "arraybuffer";

    let mseSourceBuffer = null;
    pendingBuffers[index] = [];
    isBufferProcessing[index] = false;

    ws.onopen = () => {
      console.log(`✅ WebSocket connected: ${wsUrl}`);

      if (reconnectTimers[index]) clearTimeout(reconnectTimers[index]);
      reconnectTimers[index] = setTimeout(() => {
        console.log(`🔄 WebSocket 자동 재연결 (index: ${index})`);
        stopStreamAsync(index).then(() => startPlay(index, serverIP, camUUID));
      }, RECONNECT_INTERVAL);
    };

    ws.onmessage = (event) => {
      pendingBuffers[index].push(event.data);

      if (!isBufferProcessing[index]) {
        isBufferProcessing[index] = true;
        setTimeout(() => {
          while (
            pendingBuffers[index].length > 0 &&
            !mseSourceBuffer?.updating
          ) {
            try {
              mseSourceBuffer.appendBuffer(pendingBuffers[index].shift());
            } catch (error) {
              console.error("🚨 Buffer 처리 오류:", error);
            }
          }
          isBufferProcessing[index] = false;
        }, 50);
      }
    };

    mse.addEventListener("sourceopen", () => {
      ws.onmessage = (event) => {
        const data = new Uint8Array(event.data);

        if (data[0] === 9) {
          const decodedArr = data.slice(1);
          const mimeCodec = new TextDecoder("utf-8").decode(decodedArr);

          try {
            if (!mseSourceBuffer) {
              mseSourceBuffer = mse.addSourceBuffer(
                `video/mp4; codecs="${mimeCodec}"`
              );
              mseSourceBuffer.mode = "segments";

              mseSourceBuffer.addEventListener("updateend", () => {
                if (
                  pendingBuffers[index].length > 0 &&
                  !mseSourceBuffer.updating
                ) {
                  try {
                    mseSourceBuffer.appendBuffer(pendingBuffers[index].shift());
                  } catch (error) {
                    console.error("🚨 Failed to append pending buffer:", error);
                  }
                }
              });

              while (pendingBuffers[index].length > 0) {
                if (!mseSourceBuffer.updating) {
                  mseSourceBuffer.appendBuffer(pendingBuffers[index].shift());
                }
              }
            }
          } catch (error) {
            console.error("🚨 Failed to initialize SourceBuffer:", error);
          }
        } else if (mseSourceBuffer) {
          if (!mseSourceBuffer.updating) {
            try {
              mseSourceBuffer.appendBuffer(event.data);
            } catch (error) {
              console.error("🚨 Failed to append buffer:", error);
            }
          } else {
            pendingBuffers[index].push(event.data);
          }
        }
      };
    });

    ws.onclose = () => {
      console.log(`❌ WebSocket disconnected: ${wsUrl}`);
      if (mse.readyState === "open") {
        try {
          mse.endOfStream();
        } catch (error) {
          console.error("🚨 Failed to close MediaSource:", error);
        }
      }
    };

    ws.onerror = (error) => console.error(`🚨 WebSocket error:`, error);
  };

  const stopStreamAsync = (index) => {
    return new Promise((resolve) => {
      if (!window[`ws_${index}`]) {
        console.warn(`⚠️ No WebSocket found for index ${index}, skipping.`);
        resolve();
        return;
      }

      console.log(`❌ Closing WebSocket for index ${index}...`);

      try {
        const ws = window[`ws_${index}`];

        ws.onclose = () => {
          console.log(`✅ WebSocket closed for index ${index}`);
          delete window[`ws_${index}`];
          setTimeout(resolve, 300); // 닫힌 후 약간의 딜레이 추가 (안정성 확보)
        };

        ws.onerror = (error) => {
          console.error(
            `🚨 WebSocket error while closing: index ${index}`,
            error
          );
          delete window[`ws_${index}`];
          setTimeout(resolve, 300);
        };

        ws.close();
      } catch (error) {
        console.error(`🚨 Error closing WebSocket for index ${index}:`, error);
        setTimeout(resolve, 300);
      }
    });
  };

  const cleanUpWebSockets = () => {
    const activeConnections = Object.keys(window).filter((key) =>
      key.startsWith("ws_")
    );

    if (activeConnections.length > MAX_CONNECTIONS) {
      const oldestIndex = activeConnections[0].split("_")[1];
      stopStreamAsync(oldestIndex);
    }
  };
  setInterval(cleanUpWebSockets, MAX_SESSION_DURATION);

  const parseApiData = (axiosResponse) => {
    const raw =
      typeof axiosResponse?.data === "string"
        ? JSON.parse(axiosResponse.data)
        : axiosResponse?.data;

    // 흔한 래핑키 대응
    const list = raw?.data ?? raw?.list ?? raw?.rows ?? raw;
    return {
      raw,
      list: Array.isArray(list) ? list : Array.isArray(raw) ? raw : [],
    };
  };
  const toNum = (v, def = 0) => {
    const n = Number(String(v ?? "").trim());
    return Number.isFinite(n) ? n : def;
  };

  function groupUnderListToIntervals(rows) {
    if (!Array.isArray(rows)) return [];

    const byStart = new Map();

    for (const r of rows) {
      const s = toNum(r.recvStart ?? r.recv_start ?? r.start, 0);

      if (!byStart.has(s)) {
        byStart.set(s, { start: s, workers: [] });
      }

      byStart.get(s).workers.push({
        id: r.employeeId ?? r.employeeID ?? r.empId ?? r.bid ?? undefined,
        name: r.employeeNm ?? r.employeeName ?? r.name ?? "",
        team: r.team ?? r.deptName ?? r.dept ?? "",
        callflag: toNum(r.callFlag ?? r.callflag ?? 0),
      });
    }

    return Array.from(byStart.values()).sort((a, b) => a.start - b.start);
  }

  const fetchGroundAndRulers = useCallback(async () => {
    if (!selectedWorkplaceId) return;

    const params = {
      WorkplaceId: selectedWorkplaceId,
      LoginUserId: localStorage.getItem("userid"),
    };

    try {
      const [cntRes, leftRes, rightRes] = await Promise.all([
        apiClient.post("/api/Beacon/above-employee-count", params),
        apiClient.post("/api/Beacon/under-left-employee-list", params),
        apiClient.post("/api/Beacon/under-right-employee-list", params),
      ]);

      const cnt = parseApiData(cntRes);
      const above =
        Number(
          Array.isArray(cnt.raw?.data)
            ? cnt.raw.data[0]?.aboveCnt
            : cnt.raw?.aboveCnt
        ) || 0;
      setGroundCount(Number.isFinite(above) ? above : 0);

      const left = parseApiData(leftRes);
      const right = parseApiData(rightRes);

      const leftIntervalsNew = groupUnderListToIntervals(left.list);
      const rightIntervalsNew = groupUnderListToIntervals(right.list);

      setLeftIntervals(leftIntervalsNew);
      setRightIntervals(rightIntervalsNew);

      try {
        const aboveListRes = await apiClient.post(
          "/api/Beacon/above-employee-list",
          params
        );
        const aboveParsed = parseApiData(aboveListRes);
        const normalized = (aboveParsed.list || []).map((r) => ({
          id: r.employeeId ?? r.employeeID ?? r.empId ?? r.bid ?? undefined,
          name: r.employeeNm ?? r.employeeName ?? r.name ?? "",
          //team: r.team ?? r.deptName ?? r.dept ?? "",
        }));
        setAboveList(normalized);
      } catch (e) {
        console.warn("[above-employee-list] fetch fail:", e);
        setAboveList([]); // 실패 시 빈 목록
      }

      // 디버그 로그
      console.log("★ above.raw", cnt.raw);
      console.log("★ left.raw", left.raw);
      console.log("★ right.raw", right.raw);

      console.log("★ leftIntervalsNew", leftIntervalsNew);
      console.log("★ rightIntervalsNew", rightIntervalsNew);

      console.table((left.list || []).slice(0, 20));
      console.table((right.list || []).slice(0, 20));
    } catch (e) {
      console.error("[fetchGroundAndRulers] error", e);
    }
  }, [selectedWorkplaceId]);

  useEffect(() => {
    if (!selectedWorkplaceId) return;

    let disposed = false;

    const run = () => {
      if (!disposed) fetchGroundAndRulers();
    };

    run(); // 최초 1회
    const id = setInterval(run, 1000); // 1초마다

    return () => {
      disposed = true;
      clearInterval(id);
    };
  }, [selectedWorkplaceId, fetchGroundAndRulers]);

  useEffect(() => {
    const hasKey = !!OWM_KEY;
    const hasCoords =
      Number.isFinite(workplace?.lat) && Number.isFinite(workplace?.lon);

    console.debug("[weather] deps", {
      selectedWorkplaceId,
      hasKey,
      lat: workplace?.lat,
      lon: workplace?.lon,
    });

    if (!hasKey) {
      setWeather(null);
      setWeatherErr("OWM API Key가 없습니다.");
      return;
    }
    if (!hasCoords) {
      setWeather(null);
      setWeatherErr("");
      return;
    }

    let disposed = false;
    let timer = null;

    const fetchWeather = async (retry = false) => {
      try {
        const url =
          `https://api.openweathermap.org/data/2.5/weather?lat=${workplace.lat}` +
          `&lon=${workplace.lon}&appid=${OWM_KEY}&units=metric&lang=kr`;
        console.debug("[weather] fetch:", url);

        const res = await fetch(url);
        if (res.status === 429 && !retry) {
          console.warn("[weather] 429, retry in 60s");
          timer = setTimeout(() => fetchWeather(true), 60 * 1000);
          return;
        }
        if (!res.ok) throw new Error(`OWM ${res.status}`);

        const data = await res.json();
        if (!disposed) {
          setWeather(data);
          setWeatherErr("");
          console.debug("[weather] ok", {
            temp: data?.main?.temp,
            desc: data?.weather?.[0]?.description,
          });
        }
      } catch (e) {
        if (!disposed) {
          setWeather(null);
          setWeatherErr("날씨 정보를 가져오지 못했습니다.");
        }
        console.error("weather fetch failed:", e);
      }
    };

    // 최초 1회 + 5분 주기
    fetchWeather(false);
    const interval = setInterval(() => fetchWeather(false), 5 * 60 * 1000);

    return () => {
      disposed = true;
      clearInterval(interval);
      if (timer) clearTimeout(timer);
    };
  }, [workplace?.lat, workplace?.lon, OWM_KEY, selectedWorkplaceId]);

  useEffect(() => {
    if (!selectedWorkplaceId) return;

    const fetchAll = async () => {
      // 그룹별 출입현황 count API 호출
      try {
        const { data } = await apiClient.post(
          "/api/DashBoard/all-entryexit-group-count-list",
          {
            Page: 1,
            PageSize: 100,
            OrderType: 1,
            WorkplaceId: selectedWorkplaceId,
            LoginUserId: localStorage.getItem("userid"),
            YearMonthDay: now.toISOString().slice(0, 10),
          }
        );

        const res = typeof data === "string" ? JSON.parse(data) : data;
        setGroups(
          Array.isArray(res.data)
            ? res.data.map((g) => ({
                name: g.groupName,
                count: g.groupCnt ?? g.groupCount ?? 0,
              }))
            : []
        );
      } catch (e) {
        console.error("Dashboard group count error", e);
      }

      // 잔류 출입현황 count API 호출
      try {
        const { data } = await apiClient.post(
          "/api/DashBoard/all-entryexit-remain-count-list",
          {
            Page: 1,
            PageSize: 100,
            OrderType: 1,
            WorkplaceId: selectedWorkplaceId,
            LoginUserId: localStorage.getItem("userid"),
            YearMonthDay: now.toISOString().slice(0, 10),
          }
        );

        const stayRes = typeof data === "string" ? JSON.parse(data) : data;

        setStayCnt(
          Array.isArray(stayRes.data)
            ? stayRes.data.map((g) => ({
                recount: g.remainCount,
              }))
            : []
        );
      } catch (e) {
        console.error("Dashboard remain count error", e);
      }
    };

    fetchAll();
    //pollRef.current = setInterval(fetchAll, 1000);
    pollRef.current = setInterval(fetchAll, 1000 * 6);

    return () => {
      clearInterval(pollRef.current);
    };
  }, [selectedWorkplaceId, now]);

  const columns = chunkArray(groups, CHUNK_SIZE);
  const totalVisit = groups.reduce((acc, g) => acc + Number(g.count), 0);
  const totalStay = stayCnt.reduce((acc, g) => acc + Number(g.recount), 0);

  // 밑에주석처리한거랑 관련 있는 부분
  const closePopup = () => {
    setIsPopupOpen(false);
  };
  const closeListPopup = () => {
    setIsListPopupOpen(false);
  };
  // 밑에주석처리한거랑 관련 있는 부분

  const closeAboveListPopup = () => {
    setIsAboveListPopupOpen(false);
  };

  const handleClose = () => {
    navigate(-1); // 브라우저 히스토리 기준으로 이전 페이지로 이동
  };

  const fDate = now.toLocaleDateString("ko-KR", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
  const fTime = now.toLocaleTimeString("ko-KR", {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  });

  return (
    <div className="dashboard_container">
      <div className="wrapper">
        <div className="layout_column">
          <div className="logobox row_top">
            <div className="logo_wrapper">
              <img src={logo} alt="logo" />
              <span>스마트안전관리시스템</span>
            </div>
          </div>
          <div className="layout_row cctvbox_mini">
            <div className="box width100 flex-row">
              <div
                ref={cctvRef}
                className={`cctvbox miniw ${
                  expandedIdx !== null ? "has-expanded" : ""
                }`}
                onPointerDown={onCctvPointerDown}
                onPointerMove={onCctvPointerMove}
                onPointerUp={onCctvPointerUpOrLeave}
                onPointerLeave={onCctvPointerUpOrLeave}
                onPointerCancel={onCctvPointerUpOrLeave}
                onWheel={(e) => {
                  const el = e.currentTarget;
                  if (el.scrollWidth > el.clientWidth) {
                    el.scrollLeft += e.deltaY;
                    e.preventDefault();
                  }
                }}
                style={{
                  cursor: "grab",
                  userSelect: "none",
                  touchAction: "none",
                }}
              >
                {cameraList.length === 0 ? (
                  <div>카메라 정보가 없습니다.</div>
                ) : (
                  cameraList.map((cam, idx) => {
                    const isExpanded = expandedIdx === idx;

                    return (
                      <React.Fragment key={cam.camUUID ?? idx}>
                        {/* 확대 시 배경 딤드 (같은 계층에 렌더) */}
                        {isExpanded && (
                          <div
                            className="cctv-backdrop"
                            onClick={closeExpanded}
                            style={{
                              position: "fixed",
                              inset: 0,
                              background: "rgba(0,0,0,0.55)",
                              backdropFilter: "blur(2px)",
                              zIndex: 9998,
                            }}
                          />
                        )}

                        <div
                          className={`wrapper wrap_cctv_mini ${
                            isExpanded ? "expanded" : ""
                          }`}
                          onClick={() => onTileClick(idx)}
                        >
                          {isExpanded && (
                            <button
                              className="cctv-close-btn"
                              onClick={(e) => {
                                e.stopPropagation();
                                closeExpanded();
                              }}
                              aria-label="닫기"
                            >
                              <IonIcon name="IoClose" size={20} />
                            </button>
                          )}

                          <video
                            id={`mse-video-${idx}`}
                            className={`cctv ${isExpanded ? "enlarged" : ""}`}
                            autoPlay
                            muted
                            playsInline
                            onClick={(e) => {
                              // 비디오 자체 더블클릭도 확대/축소 토글되게
                              e.stopPropagation();
                              onTileClick(idx);
                            }}
                            // 확대 상태에서 wheel/drag 같은 이벤트가 부모로 퍼져서 닫히는 걸 막음
                            onWheel={(e) => isExpanded && e.stopPropagation()}
                          />
                          {isExpanded ? (
                            <div className="cctv-title-overlay">
                              {cam.cameraName}
                            </div>
                          ) : (
                            <div className="title">{cam.cameraName}</div>
                          )}
                        </div>
                      </React.Fragment>
                    );
                  })
                )}

                {/* {cameraList.length === 0 ? (
                  <div>카메라 정보가 없습니다.</div>
                ) : (
                  cameraList.map((cam, idx) => (
                    <div
                      className="wrapper wrap_cctv_mini"
                      key={cam.camUUID ?? idx}
                    >
                      <video
                        id={`mse-video-${idx}`}
                        className="cctv"
                        autoPlay
                        muted
                        playsInline
                      />
                      <div className="title">{cam.cameraName}</div>
                    </div>
                  ))
                )} */}
                {/* 기존 작업했던 퍼블리싱소스 */}
                {/* <div className="wrapper wrap_cctv_mini">
                  <img src={cctvSample} className="cctv" alt="video" />
                  <div className="title">강원지역 수직구#2 상부_회전형</div>
                </div>
                <div className="wrapper wrap_cctv_mini">
                  <img src={cctvSample} className="cctv" alt="video" />
                  <div className="title">강원지역 수직구#2 상부_회전형</div>
                </div> */}
              </div>
            </div>
          </div>
          <div className="layout_row flex-column">
            <div className="box full-image">
              <div className="blueprintbox">
                <div className="blueprint_bgbox">
                  {/* 위치 추적을 위한 이미지를 src/images 에서 public 으로 바꿨음  */}
                  {workplace?.drawimg ? (
                    <img src={workplace.drawimg} alt="blueprint" />
                  ) : null}
                  <div
                    className="outside_people_count"
                    onMouseEnter={openAboveListInstant}
                    onMouseLeave={scheduleCloseAboveList}
                    onClick={openAboveListInstant}
                  >
                    <span>지상인원: </span>
                    <span>
                      {groundCount != null ? groundCount.toLocaleString() : "—"}{" "}
                    </span>
                    <span>명</span>
                  </div>
                </div>
              </div>
              <div className="rullers">
                <Ruler
                  length={workplace?.left_length ? workplace.left_length : 0}
                  labelInterval={
                    workplace?.left_meter_interval
                      ? workplace.left_meter_interval
                      : 0
                  }
                  style={{
                    width: `${
                      workplace?.left_percent ? workplace.left_percent : 0
                    }%`,
                  }}
                  direction="rtl"
                  setIsListPopupOpen={setIsListPopupOpen}
                  intervals={leftIntervals}
                  // intervals={[
                  //   {
                  //     start: 0,
                  //     workers: [
                  //       { name: "홍길동", team: "팀명" },
                  //       { name: "김철수", team: "팀명" },
                  //     ],
                  //   },
                  //   {
                  //     start: 400,
                  //     workers: [{ name: "이영희", team: "팀명" }],
                  //   },
                  //   {
                  //     start: 800,
                  //     workers: [
                  //       { name: "홍길동", team: "팀명" },
                  //       { name: "김철수", team: "팀명" },
                  //       { name: "홍길동", team: "팀명" },
                  //       { name: "김철수", team: "팀명" },
                  //       { name: "홍길동", team: "팀명" },
                  //       { name: "김철수", team: "팀명" },
                  //       { name: "홍길동", team: "팀명" },
                  //       { name: "김철수", team: "팀명" },
                  //       { name: "홍길동", team: "팀명" },
                  //       { name: "김철수", team: "팀명" },
                  //       { name: "홍길동", team: "팀명" },
                  //       { name: "김철수", team: "팀명" },
                  //     ],
                  //   },
                  // ]}
                />
                <Ruler
                  length={workplace?.right_length ? workplace.right_length : 0}
                  labelInterval={
                    workplace?.right_meter_interval
                      ? workplace.right_meter_interval
                      : 0
                  }
                  style={{
                    width: `${
                      workplace?.right_percent ? workplace.right_percent : 0
                    }%`,
                  }}
                  direction="ltr"
                  setIsListPopupOpen={setIsListPopupOpen}
                  // intervals={[
                  //   {
                  //     start: 0,
                  //     workers: [
                  //       { name: "홍길동", team: "팀명" },
                  //       { name: "김철수", team: "팀명" },
                  //     ],
                  //   },
                  //   {
                  //     start: 400,
                  //     workers: [{ name: "이영희", team: "팀명" }],
                  //   },
                  // ]}
                  intervals={rightIntervals}
                />
              </div>
            </div>
          </div>
        </div>
        <div className="layout_column narrow_width">
          <div className="layout_row row_top">
            <div className="watchbox">
              <div className="date">{fDate}</div>
              <div className="time">{fTime}</div>
              <button onClick={handleClose} className="closebtn">
                <IonIcon name="IoClose" />
              </button>
            </div>
          </div>
          {/* https://openweathermap.org/api 사용 처리 해놈 (김혜진 계정)*/}
          <div className="box w_box">
            <div className="w_wrapper alone">
              <div className="w_leftbox">
                <div className="w_leftbox_column icon">
                  {weather?.weather?.[0]?.icon ? (
                    // https://openweathermap.org/api 에서 제공하는 날씨 그림이 있어서 그냥 사용
                    <img
                      src={`https://openweathermap.org/img/wn/${weather.weather[0].icon}@2x.png`}
                      alt={weather.weather[0].description || "weather"}
                    />
                  ) : null}
                </div>
                <div className="w_leftbox_column">
                  <div className="w_row bigtemp">
                    {" "}
                    {weather?.main?.temp != null
                      ? `${Math.round(weather.main.temp)}°`
                      : "로딩 중..."}
                  </div>
                  <div className="w_row">
                    {weather?.weather?.[0]?.description || "날씨 정보 없음"}
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="box">
            <div className="titlebox box_titlebox">
              <div className="title">출입현황</div>
              <div className="title_right">
                <IonIcon name="IoPerson" color="#299F40" />
                <span className="spans">
                  <span className="accent">총방문</span>
                  <span>{totalVisit} · </span>
                  <span className="accent">잔류</span>
                  <span>{totalStay}</span>
                </span>
              </div>
            </div>
            <div className="innerbox row">
              {columns.map((chunk, idx) => (
                <div className="column" key={idx}>
                  <div className="visitbox">
                    <div className="type">
                      {chunk.map((g) => (
                        <span key={g.name}>
                          {g.name.length > 6
                            ? g.name.slice(0, 5) + "…"
                            : g.name}
                        </span>
                      ))}
                    </div>
                    <div className="number">
                      {chunk.map((g) => (
                        <span key={g.name}>{g.count}</span>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <div className="titlebox column box_titlebox margintop50">
              <div className="title left">환경센서</div>
            </div>
            <div className="gasbox">
              {selectedWorkplaceId ? (
                <GasSensor
                  workplaceId={selectedWorkplaceId}
                  pollMs={5000}
                  autoRotateMs={5000}
                  gap={8}
                />
              ) : (
                <div className="envboxs small">
                  <div className="env">
                    <div className="title">워크플레이스 선택 필요</div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      {/* 이거 현재로서는 불가능함 / 근태를 현장에서 안쓰고 있다고 함.. 따라서 매핑시킬수 없음 */}
      {/* 비콘 번호에 맞게 이름 수정하는 페이지 제공해야 함 */}
      {/* {isListPopupOpen && (
        <Modal
          isOpen={isListPopupOpen}
          onRequestClose={closeListPopup}
          contentLabel="작업자목록"
          ariaHideApp={false}
          className="pop_dimm_inner"
          overlayClassName="pop_dimm"
        >
          <div className="popup dark">
            <div className="popheader dark">
              <div className="titlebox">
                <div className="poptitle">
                  <IonIcon name="IoList" size={24} />
                  작업자보기
                </div>
                <button onClick={closeListPopup}>
                  <IonIcon name="IoClose" size={40} />
                </button>
              </div>
            </div>
            <div className="popbody content list">
              <div className="tablebox poptable">
                <table>
                  <thead>
                    <tr>
                      <th>소속</th>
                      <th>직급</th>
                      <th>이름</th>
                      <th>휴대폰번호</th>
                      <th>사진</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>삼일엔지니어링</td>
                      <td>상무</td>
                      <td>박현진</td>
                      <td>010-1547-6632</td>
                      <td onClick={() => setIsPopupOpen(true)}>
                        <div className="align_center color_green">
                          <IonIcon name="IoImage" size={22} color="#15E041" />
                          사진보기
                        </div>
                      </td>
                    </tr>
                    <tr>
                      <td>삼일엔지니어링</td>
                      <td>상무</td>
                      <td>박현진</td>
                      <td>010-1547-6632</td>
                      <td onClick={() => setIsPopupOpen(true)}>
                        <div className="align_center color_green">
                          <IonIcon name="IoImage" size={22} color="#15E041" />
                          사진보기
                        </div>
                      </td>
                    </tr>
                    <tr>
                      <td>삼일엔지니어링</td>
                      <td>상무</td>
                      <td>박현진</td>
                      <td>010-1547-6632</td>
                      <td onClick={() => setIsPopupOpen(true)}>
                        <div className="align_center color_green">
                          <IonIcon name="IoImage" size={22} color="#15E041" />
                          사진보기
                        </div>
                      </td>
                    </tr>
                    <tr>
                      <td>삼일엔지니어링</td>
                      <td>상무</td>
                      <td>박현진</td>
                      <td>010-1547-6632</td>
                      <td onClick={() => setIsPopupOpen(true)}>
                        <div className="align_center color_green">
                          <IonIcon name="IoImage" size={22} color="#15E041" />
                          사진보기
                        </div>
                      </td>
                    </tr>
                    <tr>
                      <td>삼일엔지니어링</td>
                      <td>상무</td>
                      <td>박현진</td>
                      <td>010-1547-6632</td>
                      <td onClick={() => setIsPopupOpen(true)}>
                        <div className="align_center color_green">
                          <IonIcon name="IoImage" size={22} color="#15E041" />
                          사진보기
                        </div>
                      </td>
                    </tr>
                    <tr>
                      <td>삼일엔지니어링</td>
                      <td>상무</td>
                      <td>박현진</td>
                      <td>010-1547-6632</td>
                      <td onClick={() => setIsPopupOpen(true)}>
                        <div className="align_center color_green">
                          <IonIcon name="IoImage" size={22} color="#15E041" />
                          사진보기
                        </div>
                      </td>
                    </tr>
                    <tr>
                      <td>삼일엔지니어링</td>
                      <td>상무</td>
                      <td>박현진</td>
                      <td>010-1547-6632</td>
                      <td onClick={() => setIsPopupOpen(true)}>
                        <div className="align_center color_green">
                          <IonIcon name="IoImage" size={22} color="#15E041" />
                          사진보기
                        </div>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </Modal>
      )} */}
      {/* {isPopupOpen && (
        <Modal
          isOpen={isPopupOpen}
          onRequestClose={closePopup}
          contentLabel="인증사진보기"
          ariaHideApp={false}
          className="pop_dimm_inner"
          overlayClassName="pop_dimm"
        >
          <div className="popup">
            <div className="popheader">
              <div className="titlebox">
                <div className="poptitle">
                  <IonIcon name="IoImage" size={24} />
                  작업자사진
                </div>
                <button onClick={closePopup}>
                  <IonIcon name="IoClose" size={40} />
                </button>
              </div>
            </div>
            <div className="popbody">
              <div className="pop_photobox">
                <img src={cctvSample} alt="인증사진" />
              </div>
            </div>
            <div className="popfooter">
              <div className="bottombtns pop">
                <button className="on" onClick={closePopup}>
                  <IonIcon name="IoCloudDownloadOutline" />
                  사진저장
                </button>
              </div>
            </div>
          </div>
        </Modal>
      )} */}
      <Modal
        isOpen={isAboveListPopupOpen}
        onRequestClose={closeAboveListPopup}
        contentLabel="작업자목록"
        ariaHideApp={false}
        className="pop_dimm_inner"
        overlayClassName="pop_dimm"
      >
        <div
          className="popup dark"
          onMouseEnter={openAboveListInstant}
          onMouseLeave={scheduleCloseAboveList}
        >
          <div className="popheader dark">
            <div className="titlebox">
              <div className="poptitle">
                <IonIcon name="IoList" size={24} />
                작업자보기
              </div>
              <button onClick={closeAboveListPopup}>
                {/* <IonIcon name="IoClose" size={40} /> */}
              </button>
            </div>
          </div>
          <div className="popbody content list">
            <div className="tablebox poptable">
              <table>
                <thead>
                  <tr>
                    <th>이름</th>
                  </tr>
                </thead>
                <tbody>
                  {aboveList.length === 0 ? (
                    <tr>
                      <td colSpan={2}>현재 지상 인원이 없습니다.</td>
                    </tr>
                  ) : (
                    aboveList.map((p, i) => (
                      <tr key={p.id ?? i}>
                        <td>{p.name || "(이름없음)"}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default DashBoardForLocation;
